import React from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { Button, Dialog, Input } from '@app/components';

import {
  ModalContainer,
  ChecklistText,
  ChecklistInfoBox,
  Footer,
  ChecklistButtonContainer,
  CancelButton,
  ModalQuesInput,
  SavingIndicator,
  QuesContainer,
  ChecklistPrimaryText,
  ChecklistInfoCard,
} from '@app/modules/transaction-details/transactions-details-components';

import { QuesInfo, ActivityLogInfo } from '@app/types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { primaryColor } from '@app/styles';

import { useUpdateChecklistMutation } from '@app/graphql';

type Props = {
  onModalClose?: any;
  Questions: QuesInfo[];
  userFirebaseId: string;
  status?: string;
  userType?: string;
  loadChecklistData: (checklistData: any, tasksPending: string) => void;
  getActivityLogData: (e: ActivityLogInfo[]) => void;
};

let activityLog: ActivityLogInfo[] = [];

export default function AdditionalQuestionsModal(props: Props) {
  const {
    onModalClose,
    Questions,
    userFirebaseId,
    loadChecklistData,
    status,
    getActivityLogData,
    userType,
  } = props;
  const { register } = useForm();
  const { transactionId }: any = useParams();
  const [updateCheckList] = useUpdateChecklistMutation();
  const [additionalQuestionsData, setAdditionalQuestionsData] = React.useState<
    QuesInfo[]
  >([]);
  const [saving, updatesaving] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!saving && Questions) {
      activityLog = [];
      const mappedQuestions = Questions.map(q => {
        return {
          ...q,
          answer: q.answer,
        };
      });
      setAdditionalQuestionsData(mappedQuestions);
    }
  }, [saving, Questions]);

  const onValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const id = event.target.id.replace('question-', '');
    const value = event.target.value;
    const questionIndex = additionalQuestionsData.findIndex(question => {
      return question.questionId === id;
    });
    if (questionIndex !== undefined) {
      const additionalQuestionsMod = [...additionalQuestionsData];
      additionalQuestionsMod[questionIndex].answer = value;
      setAdditionalQuestionsData(additionalQuestionsMod);
    }
  };

  const submitChecklistData = () => {
    updatesaving(true);
    const mappedAnswers = additionalQuestionsData.map((a: QuesInfo) => {
      return {
        answer: a.answer,
        questionId: a.questionId,
      };
    });
    const checklistVariables = {
      transactionId: transactionId,
      status: 'COMPLETED',
      assignmentChecklistKey: 'additionalQuestions',
      completedAt: moment().format('YYYY-MM-DD'),
      firebaseId: userFirebaseId,
      answers: mappedAnswers,
      activityLog: getActivityLog(),
      userType: userType,
    };
    updateCheckList({
      variables: checklistVariables,
    }).then((res: any) => {
      if (res.data.updateTransactionChecklist.success) {
        const {
          updateTransactionChecklist: { transaction },
        } = res.data;
        const { assignmentChecklist, activityLog, pendingTasks } = transaction;
        loadChecklistData(assignmentChecklist, pendingTasks);
        getActivityLogData(activityLog);
        updatesaving(false);
        setTimeout(onModalClose, 500);
      }
    });
  };

  const getActivityLog = () => {
    if (status !== 'COMPLETED') {
      activityLog.push({
        transactionId: transactionId,
        firebaseId: userFirebaseId,
        item: `Response`,
        description: 'for Additional Questions task has been submitted by ',
      });
      return activityLog;
    } else {
      if (
        JSON.stringify(additionalQuestionsData).length !==
        JSON.stringify(Questions).length
      ) {
        activityLog.push({
          transactionId: transactionId,
          firebaseId: userFirebaseId,
          item: `Response`,
          description: 'for Additional Questions task has been updated by ',
        });
      }
      return activityLog;
    }
  };

  return (
    <Dialog title="Additional Questions" onClose={onModalClose}>
      <ModalContainer>
        <ChecklistText>
          Please enter answers for the below questions.
        </ChecklistText>
        <QuesContainer>
          {additionalQuestionsData.map((data, i) => {
            return (
              <ChecklistInfoBox key={i}>
                <ChecklistInfoCard>
                  <ChecklistPrimaryText>{`${i + 1}.`}</ChecklistPrimaryText>
                  <ChecklistPrimaryText>
                    {` ${data.question}`}
                  </ChecklistPrimaryText>
                </ChecklistInfoCard>
                <ModalQuesInput>
                  <Input
                    id={'question-' + data.questionId}
                    value={data.answer}
                    placeholder="Enter Answer"
                    register={register({})}
                    onChange={onValueChange}
                  />
                </ModalQuesInput>
              </ChecklistInfoBox>
            );
          })}
        </QuesContainer>
      </ModalContainer>
      <Footer>
        {saving && (
          <SavingIndicator>
            <FontAwesomeIcon
              color={primaryColor}
              icon={faCircleNotch}
              size="lg"
              spin
            />
          </SavingIndicator>
        )}
        <CancelButton>
          <Button variant="text" size="medium" onClick={onModalClose}>
            Cancel
          </Button>
        </CancelButton>
        <ChecklistButtonContainer disabled={saving}>
          <Button
            variant="primary"
            size="medium"
            width="100%"
            type="submit"
            onClick={submitChecklistData}>
            Submit Task
          </Button>
        </ChecklistButtonContainer>
      </Footer>
    </Dialog>
  );
}
